import { useState, useEffect, useRef } from 'react';
import { supabase } from '../../lib/supabase';
import { Icon, Popover } from '../ui';

interface User {
  id: string;
  name: string;
  email: string;
}

interface AssignTaskDropdownProps {
  taskId: string;
  assignedUserId: string | null;
  assignedUserName: string | null;
  onAssign: (userId: string, userName: string) => Promise<void> | void;
  onToggleOpen?: (open: boolean) => void;
}

export default function AssignTaskDropdown(props: AssignTaskDropdownProps) {
  const { assignedUserId, assignedUserName, onAssign, onToggleOpen } = props;
  const [isOpen, setIsOpen] = useState(false);
  const [users, setUsers] = useState<User[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isAssigning, setIsAssigning] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);

  // Fetch users when dropdown is opened
  useEffect(() => {
    if (isOpen && users.length === 0) {
      fetchUsers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const fetchUsers = async () => {
    try {
      setIsLoading(true);
      const { data, error } = await supabase
        .from('users')
        .select('id, name, email')
        .order('name');

      if (error) throw error;

      setUsers((data || []).map((u) => ({ ...u, name: u.name ?? u.email })));
    } catch (err) {
      console.error('Error fetching users:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAssign = async (userId: string, userName: string) => {
    setIsAssigning(true);
    try {
      await onAssign(userId, userName);
    } finally {
      setIsAssigning(false);
      setIsOpen(false);
      onToggleOpen?.(false);
    }
  };

  return (
    <div className="assign-dropdown relative inline-block text-left" style={{ zIndex: 30 }}>
      <button
        ref={buttonRef}
        onClick={(e) => {
          e.stopPropagation();
          const newState = !isOpen;
          setIsOpen(newState);
          onToggleOpen?.(newState);
        }}
        className="pressable hover-lift"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '7px',
          height: 'var(--control-sm)',
          padding: '0 10px',
          background: 'var(--surface-raised)',
          border: '1px solid ' + (isOpen ? 'var(--accent)' : 'var(--border-hairline)'),
          borderRadius: 'var(--radius-sm)',
          color: assignedUserName ? 'var(--text-body)' : 'var(--text-muted)',
          fontFamily: 'var(--font-ui)',
          fontSize: 'var(--text-sm)',
          cursor: 'pointer',
          whiteSpace: 'nowrap',
          transition: 'border-color var(--dur-base) var(--ease-out)',
        }}
      >
        {isAssigning ? (
          <span
            className="inline-block w-3 h-3 rounded-full animate-spin"
            style={{ border: '2px solid var(--border-hairline)', borderTopColor: 'var(--accent)' }}
          />
        ) : (
          <Icon name="user" size={13} style={{ color: 'var(--text-faint)' }} />
        )}
        {assignedUserName || 'Assign'}
      </button>

      <Popover
        open={isOpen}
        anchorEl={buttonRef.current}
        onClose={() => {
          setIsOpen(false);
          onToggleOpen?.(false);
        }}
        role="menu"
        style={{ width: '208px' }}
      >
          {isLoading ? (
            <div className="text-center" style={{ padding: '12px', fontSize: 'var(--text-sm)', color: 'var(--text-faint)' }}>
              Loading users...
            </div>
          ) : (
            <div style={{ maxHeight: '320px', overflowY: 'auto' }}>
              {/* Unassign option */}
              <button
                className="hover-wash pressable"
                style={{
                  display: 'block',
                  width: '100%',
                  padding: '7px 10px',
                  border: 'none',
                  background: 'transparent',
                  borderRadius: 'var(--radius-sm)',
                  textAlign: 'left',
                  cursor: 'pointer',
                  fontFamily: 'var(--font-ui)',
                  fontSize: 'var(--text-sm)',
                  color: 'var(--danger-400)',
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  // Here you'd implement unassign functionality
                  setIsOpen(false);
                  onToggleOpen?.(false);
                }}
              >
                Unassign
              </button>

              <div style={{ borderTop: '1px solid var(--border-hairline)', margin: '4px 0' }}></div>

              {users.map(user => (
                <button
                  key={user.id}
                  className="hover-wash pressable"
                  style={{
                    display: 'block',
                    width: '100%',
                    padding: '7px 10px',
                    border: 'none',
                    background: user.id === assignedUserId ? 'var(--surface-active)' : 'transparent',
                    borderRadius: 'var(--radius-sm)',
                    textAlign: 'left',
                    cursor: 'pointer',
                    fontFamily: 'var(--font-ui)',
                    fontSize: 'var(--text-sm)',
                    color: 'var(--text-body)',
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleAssign(user.id, user.name);
                  }}
                >
                  {user.name}
                  <span className="t-num" style={{ display: 'block', fontSize: 'var(--text-2xs)', color: 'var(--text-faint)' }}>
                    {user.email}
                  </span>
                </button>
              ))}

              {users.length === 0 && (
                <div className="text-center" style={{ padding: '12px', fontSize: 'var(--text-sm)', color: 'var(--text-faint)' }}>
                  No users found
                </div>
              )}
            </div>
          )}
      </Popover>
    </div>
  );
}
